/**
 * Tâche 1206 (2e passe, Eric 2026-06-26) — Carte « Clé maîtresse de
 * chiffrement », dans la page Forfait AI.
 *
 * Eric est resté bloqué sur la procédure en ligne de commande (« je n'arrive
 * jamais à me connecter dans MongoDB ») : Emergent ne donne pas de terminal
 * dans le conteneur déployé. L'audit et la migration se font donc ici, en deux
 * clics, sans MongoDB.
 *
 * La carte se masque d'elle-même si l'utilisateur n'est pas fondateur (403) ou
 * si tout est déjà propre : elle n'apparaît que quand il y a quelque chose à
 * faire ou à savoir.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "../ui/sonner";
import { ShieldWarning, Copy } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import { API_BASE as API } from "../../lib/apiBase";

const VERDICTS = {
  clean: {
    box: "border-emerald-300 bg-emerald-50/60 text-emerald-900",
    title: "Clé maîtresse conforme",
  },
  stale_db_copy: {
    box: "border-amber-300 bg-amber-50/60 text-amber-900",
    title: "Copie inutile de la clé maîtresse en base",
  },
  needs_reencrypt: {
    box: "border-amber-300 bg-amber-50/60 text-amber-900",
    title: "Des clés API dépendent encore de la copie en base",
  },
  env_missing: {
    box: "border-red-400 bg-red-50/60 text-red-900",
    title: "Secret KNORIA_KEY_ENCRYPTION_KEY absent",
  },
  orphans: {
    box: "border-red-400 bg-red-50/60 text-red-900",
    title: "Des clés API sont indéchiffrables",
  },
};

export const MasterKeyCard = () => {
  const [report, setReport] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/byok/founder/master-key/audit`, {
        withCredentials: true,
      });
      setReport(data);
    } catch (e) {
      // 403 = pas fondateur : la carte n'existe pas pour cet utilisateur.
      setHidden(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const migrate = async () => {
    const ok = await confirmDialog({
      title: "Supprimer la copie de la clé maîtresse en base ?",
      description:
        "Les clés API encore chiffrées avec cette copie seront d'abord réencryptées avec le secret du déploiement. L'opération est refusée si une seule clé résiste.",
      confirmLabel: "Migrer maintenant",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/byok/founder/master-key/migrate`, {}, { withCredentials: true }
      );
      toast.success(
        data.reencrypted
          ? `${data.reencrypted} clé(s) réencryptée(s) — copie en base supprimée`
          : "Copie en base supprimée — le secret est désormais la seule source"
      );
      setReport(data.audit);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (hidden || !report) return null;
  // Rien à faire ET rien à signaler : on n'encombre pas la page.
  if (report.verdict === "clean") return null;

  const v = VERDICTS[report.verdict] || VERDICTS.orphans;

  return (
    <div
      className={`mb-6 rounded-xl border p-5 ${v.box}`}
      data-testid="master-key-card"
    >
      <div className="flex items-start gap-3">
        <ShieldWarning size={22} weight="duotone" className="flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold" data-testid="master-key-verdict">
            {v.title}
          </h3>
          <p className="text-sm mt-1" data-testid="master-key-action">{report.action}</p>

          <ul className="mt-3 text-xs space-y-1 opacity-90">
            <li>
              Secret d'environnement :{" "}
              <strong>{report.env_key_present ? "présent" : "ABSENT"}</strong>
            </li>
            <li>
              Copie en base :{" "}
              <strong>{report.db_copy_present ? "présente" : "absente"}</strong>
            </li>
            <li>
              Clés API chiffrées : <strong>{report.keys_total}</strong> — dont{" "}
              {report.decrypted_by_env} par le secret, {report.decrypted_by_db_copy} par la
              copie en base
            </li>
          </ul>

          {report.orphans?.length > 0 && (
            <div className="mt-3 text-xs" data-testid="master-key-orphans">
              <p className="font-semibold">Clés à ressaisir par leur organisation :</p>
              <ul className="list-disc ml-5 mt-1">
                {report.orphans.map((o) => (
                  <li key={`${o.org_id}-${o.service}`}>
                    {o.org_id} — {o.service}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.db_copy_value && (
            <div className="mt-3" data-testid="master-key-reveal">
              <p className="text-xs font-semibold">
                Valeur à coller dans Republish → Secrets → Custom Keys, sous le nom
                <code className="mx-1">KNORIA_KEY_ENCRYPTION_KEY</code> :
              </p>
              <div className="mt-1 flex items-center gap-2">
                <code className="text-xs bg-white/70 border border-current/20 rounded px-2 py-1 font-mono break-all">
                  {report.db_copy_value}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(report.db_copy_value);
                    toast.success("Valeur copiée");
                  }}
                  className="p-1.5 rounded border border-current/30 hover:bg-white/60 transition-colors"
                  data-testid="master-key-copy-btn"
                  aria-label="Copier la valeur"
                >
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-xs mt-2">
                Puis <strong>Re-publish changes</strong>, et relancez cette vérification.
              </p>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={load}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-current/30 hover:bg-white/60 transition-colors"
              data-testid="master-key-recheck-btn"
            >
              Revérifier
            </button>
            {report.can_migrate && (
              <button
                onClick={migrate}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                data-testid="master-key-migrate-btn"
              >
                {busy ? "Migration en cours…" : "Migrer et supprimer la copie"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterKeyCard;
